import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { deleteAccount, getAccountById, getAccountGroupsByUser, getPropFirmTemplates, updateAccount, useAction, useQuery } from "wasp/client/operations";
import { AlertCircle, AlertTriangle, ArrowLeft, Check, CheckCircle, Pencil, Power, Trash2 } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../client/components/ui/dialog";
import useTranslation from "../../client/hooks/useTranslation";
import { useToast } from "../../client/hooks/use-toast";
import { normalizeSymbol } from "../../shared/utils";
import { CURRENCY } from "../../shared/currency";
import RuleUpdateBanner from "../../trading-accounts/components/RuleUpdateBanner";
import NoRulesBanner from "../../trading-accounts/components/NoRulesBanner";
import AccountResetSuggestionBanner from "../../trading-accounts/AccountResetSuggestionBanner";
import AccountSourceBadge from "../../trading-accounts/AccountSourceBadge";
import BalanceFreshnessHint from "../../trading-accounts/BalanceFreshnessHint";
import BalanceGapHint from "../../trading-accounts/BalanceGapHint";
import PromoteToFundedModal from "../../trading-accounts/PromoteToFundedModal";
import TradovateSyncStatus from "../../trading-accounts/tradovate/TradovateSyncStatus";
import { AccountProgressBar, type AxisTone } from "../../trading-accounts/components/AccountProgressBar";
import { resolveBounds } from "../../trading-accounts/accountProgressGeometry";
import { deactivationBadge, deactivationLabels } from "../../trading-accounts/deactivationBadge";
import { isPropFirmFundedStatus } from "../../trading-accounts/accountStatus";
import { amountOrPercent, resolveEffectiveTierRules } from "../../trading-accounts/effectiveTierRules";
import { PayoutsSection } from "../../trading-accounts/AccountDetailPage";
import { EditAccountModal } from "../../trading-accounts/TradingAccountsPage";
import type { EnrichedAccount, EnrichedGroup, PropFirmTemplateSnapshot } from "../../trading-accounts/accountTypes";
import { AttachmentsSection } from "../../account-attachments/components/AttachmentsSection";
import { useLiteData } from "../data/LiteDataProvider";
import { BalanceChart } from "../ui/Charts";
import { Card, Empty, FirmMark, cardCls, toneColor, type Tone } from "../ui/ui";
import { pnlClass } from "../lib/format";
import { trailingFloorSeries } from "../lib/trailingFloor";

/**
 * Détails d'un compte, version Lite. Mêmes données et mêmes actions que la page
 * Advanced, rangées par question : où j'en suis (en-tête), comment je trade
 * (performance), quelles règles je dois tenir, ce que le compte m'a rapporté.
 *
 * La progression mesurée (objectif, jours, marge, payout) vit UNE seule fois,
 * sous l'axe de l'en-tête : l'Advanced la répétait dans la colonne des règles.
 */
export default function LiteAccountDetailPage() {
  const t = useTranslation();
  const tl = t.lite.accountDetail;
  const ta = t.tradingAccounts;
  const { accountId = "" } = useParams<{ accountId: string }>();
  const { fmt, rawAccounts, refetchAll } = useLiteData();
  const { toast } = useToast();
  const updateAccountFn = useAction(updateAccount);

  const { data: account, isLoading, refetch } = useQuery(getAccountById, { id: accountId }, { enabled: !!accountId });
  const [editOpen, setEditOpen] = useState(false);
  const { data: groups = [] } = useQuery(getAccountGroupsByUser, undefined, { enabled: editOpen });
  const { data: templates = [] } = useQuery(getPropFirmTemplates, undefined, { enabled: editOpen });
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const [promoteOpen, setPromoteOpen] = useState(false);

  const refresh = useCallback(() => { refetch(); refetchAll(); }, [refetch, refetchAll]);

  const setActive = useCallback(async (isActive: boolean) => {
    if (!account) return;
    try {
      await updateAccountFn(isActive
        ? { id: account.id, isActive: true, disabledAt: null }
        : { id: account.id, isActive: false, disabledAt: (account as any).disabledAt ? undefined : new Date().toISOString() });
      toast({ title: isActive ? ta.accountActivated : ta.accountDeactivated });
      setConfirmDeactivate(false);
      refresh();
    } catch (err: any) {
      toast({ title: ta.error, description: err?.message, variant: "destructive" });
    }
  }, [account, updateAccountFn, toast, ta, refresh]);

  // Suppression : même action et mêmes messages que la page Comptes Advanced.
  const handleDelete = useCallback(async () => {
    if (!account) return;
    try {
      await deleteAccount({ id: account.id });
      toast({ title: ta.accountDeleted, description: ta.accountDeletedDescription });
      setConfirmDelete(false);
      refetchAll();
      navigate("/trading-accounts");
    } catch (err: any) {
      toast({ title: ta.error, description: err?.message || ta.deleteError, variant: "destructive" });
    }
  }, [account, toast, ta, refetchAll, navigate]);

  const a = account as any;
  const editable = useMemo(
    () => (rawAccounts.find(r => r.id === accountId) ?? a ?? null) as EnrichedAccount | null,
    [rawAccounts, accountId, a],
  );

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[300px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }
  if (!a) {
    return (
      <div className="pt-6 space-y-4">
        <BackLink label={tl.back} />
        <div className={cardCls}><Empty title={tl.notFound} /></div>
      </div>
    );
  }

  const challenge = a.challengeProgress;
  const buffer = a.bufferStatus;
  const payout = a.payoutProgress;
  const template = a.effectiveTemplate;
  const isPropFirm = !!template;
  const isChallenge = a.status === "challenge";
  const isFunded = isPropFirmFundedStatus(a.status, isPropFirm);
  const objectiveBase = a.activeTier?.accountSize ?? a.initialBalance;
  const bounds = resolveBounds({
    status: a.status,
    initialBalance: a.initialBalance,
    objectiveBase,
    buffer: buffer ?? null,
    challengeTargetAmount: challenge?.targetAmount ?? null,
    payoutTargetAmount: payout?.targetAmount ?? null,
  });
  const axisTone: AxisTone = buffer?.isViolation ? "violation" : buffer?.isWarning ? "warning" : "normal";
  const balancePnl = a.currentBalance - a.initialBalance;
  const tradesVariation = a.initialBalance > 0 ? a.totalPnl / a.initialBalance : 0;
  const statusLabel = ({ challenge: ta.statusChallenge, pa: ta.statusPa, inactive: ta.statusInactive, demo: ta.statusDemo, live: ta.statusLive } as Record<string, string>)[a.status] ?? ta.statusLive;
  const deactivation = !a.isActive ? deactivationBadge(a.deactivatedReason, deactivationLabels(t)) : null;
  const phase = a.resolvedPhase;

  // ── Progression MESURÉE : chaque jauge apparaît sous les mêmes conditions que dans l'Advanced.
  const progress: ProgressItem[] = [];
  if (isChallenge && challenge && (challenge.targetAmount !== null || challenge.targetPercent !== null)) {
    progress.push({
      label: ta.profitTarget,
      value: challenge.targetAmount !== null
        ? `${fmt.money(challenge.profit, { sign: false })} / ${fmt.money(challenge.targetAmount, { sign: false })}`
        : `${fmt.pct(challenge.profitPercent / 100, 2)} / ${challenge.targetPercent} %`,
      ratio: challenge.ratio,
      done: challenge.ratio >= 1,
    });
  }
  if (challenge && challenge.daysTraded > 0) {
    progress.push({
      label: ta.tradingDays,
      value: challenge.minTradingDays !== null ? `${challenge.daysTraded} / ${challenge.minTradingDays}` : ta.daysTradedCount.replace("{count}", String(challenge.daysTraded)),
      ratio: challenge.minTradingDays !== null ? challenge.daysRatio : undefined,
      done: challenge.minTradingDays !== null && challenge.daysRatio >= 1,
    });
  }
  if (buffer && buffer.ruleType !== null) {
    progress.push({
      label: ta.bufferDrawdown,
      value: `${fmt.money(buffer.remainingRoom, { sign: false })} ${tl.remaining}`,
      ratio: buffer.usedRatio,
      tone: buffer.isViolation ? "loss" : buffer.isWarning ? "warn" : "brand",
    });
  }
  if (isFunded && payout && payout.targetAmount !== null) {
    progress.push({
      label: ta.payoutTarget,
      value: `${fmt.money(payout.currentProfit, { sign: false })} / ${fmt.money(payout.targetAmount, { sign: false })}`,
      ratio: payout.ratio,
      done: !!payout.isReached,
    });
  }
  const hasQualifying = a.qualifyingDays != null && phase?.minWinningDaysForPayout != null && phase?.minWinningDayProfit != null;
  if (hasQualifying) {
    progress.push({
      label: `${ta.qualifyingDaysLabel} (${fmt.money(phase.minWinningDayProfit, { sign: false })}+)`,
      value: `${a.qualifyingDays} / ${phase.minWinningDaysForPayout}`,
      ratio: a.qualifyingDays / phase.minWinningDaysForPayout,
      done: a.qualifyingDays >= phase.minWinningDaysForPayout,
    });
  }

  // ── Notes d'état : elles qualifient la progression, la phrase dit quoi faire.
  const notes: { tone: "gain" | "warn" | "loss" | "brand"; text: string }[] = [];
  if (isChallenge && challenge?.isComplete) notes.push({ tone: "gain", text: ta.challengeComplete });
  if (buffer && buffer.ruleType !== null && buffer.isViolation) notes.push({ tone: "loss", text: ta.drawdownViolation });
  if (buffer && buffer.ruleType !== null && buffer.isWarning && !buffer.isViolation) notes.push({ tone: "warn", text: ta.drawdownWarning.replace("{percent}", Math.round(buffer.usedRatio * 100).toString()) });
  if (isFunded && payout && payout.targetAmount !== null && payout.isReached) notes.push({ tone: "gain", text: ta.payoutReached });
  if (hasQualifying && a.qualifyingDays >= phase.minWinningDaysForPayout) notes.push({ tone: "brand", text: ta.payoutEligible });

  // Trailing sans payout : le plancher suit le pic, point par point, gelé au
  // plancher du moteur. Après un payout le plancher peut avoir été figé à cet
  // instant (drawdownLock.ts) : on garde alors la ligne statique.
  const trailingLive = buffer?.ruleType === "trailing_drawdown" && !((a.totalPayouts ?? 0) > 0) && buffer.maxAllowedLoss > 0;
  const floorSeries = trailingLive
    ? trailingFloorSeries(((a.equityCurve ?? []) as { balance: number }[]).map(p => p.balance), { initialBalance: a.initialBalance, maxDrawdown: buffer.maxAllowedLoss, enginePeak: buffer.highWaterMark, floorCap: buffer.floorAmount })
    : null;
  const curve = ((a.equityCurve ?? []) as { date: string; balance: number }[]).map(p => ({ label: fmt.dateShort(p.date), balance: p.balance }));

  return (
    <div className="pt-2 md:pt-4 pb-8 space-y-5 lite-stagger">
      <RuleUpdateBanner accountId={a.id} />
      <NoRulesBanner accounts={[{ id: a.id, name: a.name, hasNoRules: a.hasNoRules }]} />
      <AccountResetSuggestionBanner accountId={a.id} />

      {/* Fil + actions */}
      <div className="flex items-center justify-between gap-3">
        <BackLink label={tl.back} />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => (a.isActive ? setConfirmDeactivate(true) : setActive(true))}>
            <Power className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{a.isActive ? ta.deactivateAccount : ta.activateAccount}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{ta.editAccount}</span>
          </Button>
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => setConfirmDelete(true)} title={ta.delete} aria-label={ta.delete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ── 1. Où j'en suis ── */}
      <section className={`${cardCls} p-5 md:p-6 space-y-6`}>
        <div className="flex items-start gap-3 flex-wrap">
          <FirmMark firm={template ? { key: template.id, id: template.id, slug: template.slug, name: template.name, color: template.color, textColor: template.textColor } : null} />
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-xl md:text-2xl font-bold tracking-tight break-words">{a.name}</h1>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
              {template && <span className="text-xs text-muted-foreground mr-1">{template.name}{a.activeTier?.label ? ` · ${a.activeTier.label}` : ""}</span>}
              <Chip>{statusLabel}</Chip>
              {phase && <Chip tone="brand">{phase.name}</Chip>}
              <AccountSourceBadge account={a} />
              {!a.isActive && <Chip>{ta.statusInactive}</Chip>}
              {deactivation && <span title={deactivation.hint}><Chip>{deactivation.label}</Chip></span>}
              {buffer?.isViolation && <Chip tone="loss"><AlertTriangle className="h-3 w-3" />{ta.maxLossExceeded}</Chip>}
              <BalanceFreshnessHint brokerBalance={a.brokerBalance} brokerBalanceAt={a.brokerBalanceAt} />
            </div>
          </div>
          {isChallenge && <Button size="sm" className="w-full sm:w-auto order-last sm:order-none" onClick={() => setPromoteOpen(true)}>{ta.promoteToFunded}</Button>}
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <div className="text-xs text-muted-foreground">{tl.balance}</div>
            <div className="font-mono text-3xl md:text-4xl font-semibold tabular-nums mt-1">{fmt.money(a.currentBalance, { sign: false, decimals: true })}</div>
            <div className={`font-mono text-sm mt-1 ${pnlClass(balancePnl)}`}>{fmt.money(balancePnl, { decimals: true })} <span className="text-muted-foreground font-sans">{tl.sinceStart}</span></div>
          </div>
          {isPropFirm && challenge && (
            <div className="sm:text-right" title={tl.objectiveProfitHint}>
              <div className="text-xs text-muted-foreground">{tl.objectiveProfit}</div>
              <div className={`font-mono text-2xl font-semibold tabular-nums mt-1 ${pnlClass(challenge.profit)}`}>{fmt.money(challenge.profit, { decimals: true })}</div>
              <div className="text-xs text-muted-foreground/80 mt-1 sm:ml-auto max-w-xs">{tl.objectiveProfitHint}</div>
            </div>
          )}
        </div>

        {bounds && <AccountProgressBar bounds={bounds} currentBalance={a.currentBalance} currency={CURRENCY} tone={axisTone} size="lg" />}

        {progress.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-3">{tl.progress}</div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
              {progress.map(p => <ProgressTile key={p.label} item={p} doneLabel={tl.done} />)}
            </div>
          </div>
        )}

        {notes.length > 0 && (
          <div className="space-y-2">
            {notes.map(n => (
              <div key={n.text} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm" style={{ background: toneColor(n.tone, 0.1), color: toneColor(n.tone) }}>
                {n.tone === "warn" || n.tone === "loss" ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <CheckCircle className="h-4 w-4 shrink-0" />}
                {n.text}
              </div>
            ))}
          </div>
        )}

        <BalanceGapHint reconciliation={a.balanceReconciliation} formatCurrency={v => fmt.money(v, { sign: false, decimals: true })} className="text-xs" />
      </section>

      {a.challengeRecap && (
        <Card title={ta.challengeRecapTitle}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            <Metric label={ta.challengeRecapFinalBalance} value={fmt.money(a.challengeRecap.finalBalance, { sign: false, decimals: true })} />
            <Metric label={ta.challengeRecapNetProfit} value={fmt.money(a.challengeRecap.netProfit, { decimals: true })} className="text-pnlPositive" />
            <Metric label={ta.challengeRecapTradeCount} value={a.challengeRecap.tradeCount} />
            <Metric label={ta.challengeRecapWinRate} value={fmt.pct(a.challengeRecap.winRate / 100, 1)} />
            <Metric label={ta.challengeRecapDuration} value={ta.challengeRecapDurationDays.replace("{days}", String(a.challengeRecap.durationDays))} />
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 space-y-5 min-w-0">
          {/* ── 2. Comment je trade ── */}
          <Card title={tl.performance}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
              <Metric label={ta.tradesCount} value={a.tradesCount} />
              <Metric label={ta.winRate} value={fmt.pct((a.winRate ?? 0) / 100, 1)} hint={`${a.wins ?? 0}W / ${a.losses ?? 0}L`} />
              <Metric label={ta.profitFactor} value={a.profitFactor != null ? fmt.num(a.profitFactor) : "-"} />
              <Metric label={ta.tradesPnl} value={fmt.money(a.totalPnl, { decimals: true })} className={pnlClass(a.totalPnl)} hint={`${tradesVariation >= 0 ? "+" : ""}${fmt.pct(tradesVariation, 2)}`} />
              <Metric label={ta.avgWin} value={fmt.money(a.avgWin ?? 0, { sign: false, decimals: true })} className="text-pnlPositive" />
              <Metric label={ta.avgLoss} value={fmt.money(-(Math.abs(a.avgLoss ?? 0)), { decimals: true })} className="text-pnlNegative" />
              <Metric label={ta.maxDrawdown} value={fmt.money(-(Math.abs(a.maxDrawdown ?? 0)), { decimals: true })} />
              <Metric label={ta.totalPayoutsReceived} value={fmt.money(a.totalPayouts ?? 0, { sign: false, decimals: true })} />
            </div>
          </Card>

          <Card title={tl.balanceCurve}>
            {curve.length ? (
              <BalanceChart data={curve} start={a.initialBalance} floor={bounds?.floor ?? null} floorSeries={floorSeries} floorLabel={ta.equityCurveMaxLossLimit} startLabel={tl.start} />
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">{ta.noEquityData}</p>
            )}
          </Card>

          {/* ── 4. Ce que le compte m'a rapporté ── (bloc Advanced réutilisé : suggestions, questions, justificatifs) */}
          <div className="[&>*]:rounded-2xl">
            <PayoutsSection account={a} t={t} onPayoutAdded={refresh} onPayoutDeleted={refresh} />
          </div>

        </div>

        <div className="space-y-5 min-w-0">
          {/* ── 3. Les règles à tenir ── */}
          <RulesCard account={a} />
          <Card title={tl.symbols} pad={false}>
            <SymbolTable stats={a.symbolStats} />
          </Card>
          {a.tradovateSyncedAccount?.connectionId && (
            <Card title={tl.sync}><TradovateSyncStatus connectionId={a.tradovateSyncedAccount.connectionId} /></Card>
          )}
        </div>
      </div>

      <div className="[&>*]:rounded-2xl"><AttachmentsSection accountId={a.id} /></div>

      {/* Fenêtres */}
      <Dialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ta.deactivateAccountConfirmTitle.replace("{name}", a.name)}</DialogTitle>
            <DialogDescription>{ta.deactivateAccountConfirmDescription}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeactivate(false)}>{ta.cancel}</Button>
            <Button onClick={() => setActive(false)}><Power className="h-4 w-4 mr-2" />{ta.deactivate}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ta.deleteAccountConfirmTitle.replace("{name}", a.name)}</DialogTitle>
            <DialogDescription>{ta.deleteAccountConfirmDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
              <p className="text-sm text-destructive">{ta.deleteAccountConfirmWarning}</p>
            </div>
            {a.isActive && (
              <div className="flex gap-2 rounded-md border border-border bg-muted/40 p-3">
                <Power className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <p className="text-sm text-muted-foreground">{ta.deleteAccountConfirmHint}</p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>{ta.cancel}</Button>
            {a.isActive && (
              <Button variant="secondary" onClick={() => { setConfirmDelete(false); setConfirmDeactivate(true); }}>
                <Power className="h-4 w-4 mr-2" />{ta.deactivateInstead}
              </Button>
            )}
            <Button variant="destructive" onClick={handleDelete}>{ta.delete}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PromoteToFundedModal
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        accountId={a.id}
        initialBalance={a.initialBalance}
        trades={(a.trades ?? []).map((tr: any) => ({ date: typeof tr.date === "string" ? tr.date : new Date(tr.date).toISOString() }))}
        onPromoted={() => { setPromoteOpen(false); refresh(); }}
      />
      <EditAccountModal
        account={editOpen ? editable : null}
        groups={groups as EnrichedGroup[]}
        templates={templates as PropFirmTemplateSnapshot[]}
        onClose={() => setEditOpen(false)}
        onSuccess={refresh}
      />
    </div>
  );
}

function BackLink({ label }: { label: string }) {
  return (
    <Link to="/trading-accounts" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
      <ArrowLeft className="h-4 w-4" />{label}
    </Link>
  );
}

function Chip({ children, tone }: { children: ReactNode; tone?: "brand" | "loss" }) {
  const cls = tone === "brand" ? "bg-primary/10 text-primary" : tone === "loss" ? "bg-pnlNegative/10 text-pnlNegative" : "bg-muted text-muted-foreground";
  return <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{children}</span>;
}

function Metric({ label, value, hint, className = "" }: { label: string; value: ReactNode; hint?: string; className?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground truncate">{label}</div>
      <div className={`font-mono tabular-nums text-lg font-semibold mt-1 truncate ${className}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground/70 mt-0.5 font-mono">{hint}</div>}
    </div>
  );
}

interface ProgressItem { label: string; value: string; ratio?: number; tone?: Tone; done?: boolean }

function ProgressTile({ item, doneLabel }: { item: ProgressItem; doneLabel: string }) {
  const tone: Tone = item.done ? "gain" : item.tone ?? "brand";
  return (
    <div className="rounded-xl bg-muted/50 px-3.5 py-3 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground truncate">{item.label}</span>
        {item.done && <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-pnlPositive shrink-0"><Check className="h-3 w-3" />{doneLabel}</span>}
      </div>
      <div className="font-mono text-sm font-semibold mt-1.5 truncate">{item.value}</div>
      {item.ratio !== undefined && (
        <div className="h-1.5 rounded-full bg-background/60 overflow-hidden mt-2.5">
          <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.max(0, Math.min(1, item.ratio)) * 100}%`, background: toneColor(tone) }} />
        </div>
      )}
    </div>
  );
}

/** Règles DÉCLARÉES du palier, en liste. La progression mesurée est dans l'en-tête. */
function RulesCard({ account }: { account: any }) {
  const t = useTranslation();
  const ta = t.tradingAccounts;
  const { fmt } = useLiteData();
  const rules = resolveEffectiveTierRules(account);
  if (!rules) return null;
  const ib = account.initialBalance;
  const money = (v: number | null) => (v === null ? null : fmt.money(v, { sign: false }));
  const days = (v: number | null) => (v === null ? null : ta.daysCountShort.replace("{count}", String(v)));
  const rows: [string, string | null][] = [
    // Sur un challenge, l'objectif est déjà MESURÉ dans l'en-tête ; ailleurs il reste une règle déclarée.
    [ta.profitTarget, account.status === "challenge" ? null : money(amountOrPercent(rules.profitTargetAmount, rules.profitTargetPercent, ib))],
    [ta.maxDailyLoss, money(amountOrPercent(rules.maxDailyLossAmount, rules.maxDailyLossPercent, ib))],
    [ta.maxTotalLoss, money(amountOrPercent(rules.maxTotalLossAmount, rules.maxTotalLossPercent, ib))],
    [ta.trailingDrawdown, money(rules.trailingDrawdown)],
    [ta.minTradingDays, days(rules.minTradingDays)],
    [ta.payoutThreshold, money(rules.payoutThreshold)],
    [ta.payoutCooldown, days(rules.payoutCooldownDays)],
    [ta.consistencyRule, rules.consistencyRulePercent === null ? null : ta.consistencyRuleValue.replace("{percent}", String(rules.consistencyRulePercent))],
    [ta.maxContracts, rules.maxContracts === null ? null : String(rules.maxContracts)],
  ];
  const visible = rows.filter((r): r is [string, string] => r[1] !== null);
  return (
    <Card
      title={t.lite.accountDetail.rules}
      subtitle={account.ruleSource === "snapshot" && account.ruleSnapshotAt ? `${ta.rulesFrozenBadge} · ${new Date(account.ruleSnapshotAt).toLocaleDateString()}` : undefined}
    >
      <dl className="divide-y divide-border">
        {visible.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-3 py-2.5 first:pt-0 last:pb-0 text-sm">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-mono font-medium text-right">{value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function SymbolTable({ stats }: { stats: Record<string, { pnl: number; count: number; wins: number }> | null | undefined }) {
  const ta = useTranslation().tradingAccounts;
  const { fmt } = useLiteData();
  const rows = Object.entries(stats ?? {}).sort((x, y) => Math.abs(y[1].pnl) - Math.abs(x[1].pnl)).slice(0, 10);
  if (!rows.length) return <p className="text-sm text-muted-foreground px-5 pb-5">{ta.noSymbolStats}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-muted-foreground border-b border-border">
            <th className="text-left font-medium px-5 py-2.5">{ta.symbol}</th>
            <th className="text-right font-medium px-3 py-2.5">{ta.trades}</th>
            <th className="text-right font-medium px-3 py-2.5">{ta.winRate}</th>
            <th className="text-right font-medium px-5 py-2.5">{ta.pnl}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([symbol, s]) => (
            <tr key={symbol} className="border-b border-border last:border-0">
              <td className="px-5 py-2.5 font-medium">{normalizeSymbol(symbol)}</td>
              <td className="px-3 py-2.5 text-right font-mono">{s.count}</td>
              <td className="px-3 py-2.5 text-right font-mono">{s.count > 0 ? fmt.pct(s.wins / s.count) : "-"}</td>
              <td className={`px-5 py-2.5 text-right font-mono ${pnlClass(s.pnl)}`}>{fmt.money(s.pnl, { decimals: true })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
